/* eslint-disable security/detect-object-injection */
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Autosuggestions } from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { ProductRefiner } from '@msdyn365-commerce/retail-proxy';
import { getProductSearchRefinersAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { createSearchStateInput } from '@msdyn365-commerce-modules/search-utilities';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, KeyCodes } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import * as React from 'react';

import getSearchSuggestionsAction, { AutoSuggestInput, SuggestionType } from './actions/get-auto-suggest-suggestions';
import {
    CategorySuggestionsComponent,
    FormComponent,
    ISearchCategoryViewProps,
    ISearchKeywordViewProps,
    ISearchProductViewProps,
    KeywordSuggestionsComponent,
    LabelComponent,
    ProductSuggestionsComponent,
    OtherCurriculaSuggestionsComponent
} from './components';
import { IMoeSearchData } from './moe-search.data';
import { IMoeSearchProps, ISuggestionTypeCriterionData } from './moe-search.props.autogenerated';
import { getToggleLang, getUserPreferredLanguage, getUserPrefLang } from '../../utilities/language-change';
import { KWR, SEARCH } from '../../utilities/constants';

export interface ISearchState {
    isSearchFormExpanded: boolean;
    searchText: string;
    searchKeywordSuggestClass: string;
    searchProductSuggestClass: string;
    searchCategorySuggestClass: string;
    suggestions: Autosuggestions | undefined;
    searchRefiners?: ProductRefiner[] | undefined;
    autoSuggestAriaLabel: string;
    isInitialState: boolean;
    isLoadingAutoSuggest?: boolean;
    searchCurriculaSuggestClass: string;
}

export interface ISearchViewProps extends IMoeSearchProps<IMoeSearchData> {
    Search: IModuleProps;
    AutoSuggestAriaLabel: INodeProps;
    AutoSuggestAriaLabelText: string;
    searchText: string;
    AutoSuggest: INodeProps;
    KeywordSuggest: INodeProps;
    ProductSuggest: INodeProps;
    CategorySuggest: INodeProps;
    SearchForm: INodeProps;
    FormWrapper: INodeProps;
    UlKeyword: INodeProps;
    UlProduct: INodeProps;
    UlCategory: INodeProps;
    label: React.ReactNode;
    form: React.ReactNode;
    autosuggestKeyword?: ISearchKeywordViewProps;
    autosuggestProduct?: ISearchProductViewProps;
    autosuggestCategory?: ISearchCategoryViewProps;
    autosuggestOtherCurricula?: any;
    callbacks: {
        handleCancelSearchChange(): void;
    };
    isSearchFormExpanded: boolean;
    isLoadingAutoSuggest?: boolean;
    isLoadingNode?: React.ReactNode;
    CurriculaSuggest?: INodeProps;
}

/**
 *
 * Search module.
 * @extends {React.Component<IMoeSearchProps<IMoeSearchData>, ISearchState>}
 */
class Search extends React.Component<IMoeSearchProps<IMoeSearchData>, ISearchState> {
    private readonly searchTextInput: React.RefObject<HTMLInputElement>;

    private readonly searchCollapsedLabel: React.RefObject<HTMLButtonElement>;

    private readonly formReference: React.RefObject<HTMLButtonElement>;

    private readonly maxChars: number;

    private readonly inputSuggestionType?: ISuggestionTypeCriterionData[];

    private readonly topResultsCount?: number;

    private readonly hitPrefix: string = '{';

    private readonly hitSuffix: string = '}';

    private readonly waitTime: number = 500;

    private readonly autoSuggestResultScreenReaderDiv: React.RefObject<HTMLDivElement>;

    private readonly autoSuggestResultDiv: React.RefObject<HTMLDivElement>;

    private readonly searchFormClass: string;

    private readonly searchLabelClass: string;

    private readonly telemetryContent: ITelemetryContent;

    // @ts-expect-error
    private cancellabelDebounce: debounce;

    constructor(props: IMoeSearchProps<IMoeSearchData>) {
        super(props);
        this.searchTextInput = React.createRef();
        this.searchCollapsedLabel = React.createRef();
        this.autoSuggestResultScreenReaderDiv = React.createRef();
        this.autoSuggestResultDiv = React.createRef();
        this.formReference = React.createRef();
        this.state = {
            isSearchFormExpanded: false,
            searchText: '',
            searchKeywordSuggestClass: 'ms-search__autoSuggest__keyword',
            searchProductSuggestClass: 'ms-search__autoSuggest__product',
            searchCategorySuggestClass: 'ms-search__autoSuggest__category',
            searchCurriculaSuggestClass: 'ms-search__autoSuggest__curricula',
            suggestions: undefined,
            searchRefiners: undefined,
            autoSuggestAriaLabel: '',
            isInitialState: true,
            isLoadingAutoSuggest: false
        };
        this.maxChars =
            (this.props.context &&
                this.props.context.app &&
                this.props.context.app.config &&
                this.props.context.app.config.searchInputMaxLength) ||
            100;
        this.inputSuggestionType = this.props.config && this.props.config.suggestionTypeCriterion;
        this.topResultsCount = this.props.config && this.props.config.topResultsCount ? this.props.config.topResultsCount : 5;
        this.searchFormClass = 'ms-search__form';
        this.searchLabelClass = 'ms-search__label';
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public async componentDidMount(): Promise<void> {
        document.body && document.body.addEventListener('mousedown', this._focusOutTarget);
        window && window.addEventListener('keyup', this._keyup);
    }

    public componentWillUnmount(): void {
        document.body && document.body.removeEventListener('mousedown', this._focusOutTarget, false);
        window && window.removeEventListener('keyup', this._keyup, false);
    }

    public componentDidUpdate(): void {
        const {
            config: { hideSearchLabel }
        } = this.props;
        const { isSearchFormExpanded, isInitialState } = this.state;

        if (!this.props.context.request.params.isEditor) {
            if (!hideSearchLabel && isSearchFormExpanded) {
                this.searchTextInput && this.searchTextInput.current && this.searchTextInput.current.focus();
            } else if (!isInitialState) {
                this.searchCollapsedLabel && this.searchCollapsedLabel.current && this.searchCollapsedLabel.current.focus();
            }
        }
    }

    public shouldComponentUpdate(nextProps: IMoeSearchProps<IMoeSearchData>, nextState: ISearchState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    // eslint-disable-next-line complexity -- ignore complexity.
    public render(): JSX.Element {
        const {
            context,
            resources: {
                searchtextInMaori,
                searchLabelArialLabelInMaori,
                cancelBtnAriaLabelInMaori,
                searchBtnAriaLabelInMaori,
                submitBtnAriaLabelInMaori
            },
            config: { hideSearchLabel, disableSubmitSearch, searchplaceholderText, shouldShowFullCategoryPath, className = '', siteName }
        } = this.props;

        // Identifying Language
        const toggleLang = getToggleLang(context);
        const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
        const preferredLanguage: any = getUserPreferredLanguage(siteName as any, SEARCH, userPrefLang, toggleLang, context);

        const {
            isSearchFormExpanded,
            searchText,
            searchKeywordSuggestClass,
            searchProductSuggestClass,
            searchCategorySuggestClass,
            suggestions,
            searchRefiners,
            isLoadingAutoSuggest,
            searchCurriculaSuggestClass
        } = this.state;

        const searchQsp = (context && context.app && context.app.config && context.app.config.searchQueryStringParameter) || 'q';
        const searchPageURL = getUrlSync('search', context && context.actionContext) || '';
        const searchURL = this._appendQueryParams(searchPageURL, searchText, searchQsp);
        let searchPlaceholder: string | undefined;

        if (preferredLanguage !== 'maori') {
            searchPlaceholder = searchplaceholderText && searchplaceholderText.length > 0 ? searchplaceholderText : ' ';
        } else {
            searchPlaceholder = searchplaceholderText && searchplaceholderText.length > 0 ? searchplaceholderText : ' ';
        }

        if (!searchPlaceholder) {
            searchPlaceholder = searchplaceholderText;
        }

        const keywordSuggestions =
            suggestions &&
            suggestions.AllSearchResults &&
            suggestions.AllSearchResults.filter(
                i => i.SuggestionType === SuggestionType.Keyword || i.SuggestionType === SuggestionType.None
            );
        const productSuggestions =
            suggestions &&
            suggestions.AllSearchResults &&
            suggestions.AllSearchResults.filter(i => {
                if (i.SuggestionType === SuggestionType.Product) {
                    const index: any = i.ExtensionProperties?.findIndex(p => p.Key === 'ChannelId');
                    if (
                        index > -1 &&
                        i &&
                        i?.ExtensionProperties &&
                        i?.ExtensionProperties?.length > 0 &&
                        i?.ExtensionProperties[index]?.Value?.LongValue !== 0
                    ) {
                        return i?.ExtensionProperties[index]?.Value?.LongValue === this.props.context.request.apiSettings.channelId;
                    }
                }
                return i.SuggestionType === SuggestionType.Product;
            });
        const categorySuggestions =
            suggestions &&
            suggestions.AllSearchResults &&
            suggestions.AllSearchResults.filter(i => i.SuggestionType === SuggestionType.Category);
        const otherCurriculaSuggestions =
            suggestions &&
            suggestions.AllSearchResults &&
            suggestions.AllSearchResults.filter(i => {
                if (i.SuggestionType === SuggestionType.Product) {
                    const index: any = i.ExtensionProperties?.findIndex(p => p.Key === 'ChannelId');
                    if (
                        index > -1 &&
                        i &&
                        i?.ExtensionProperties &&
                        i?.ExtensionProperties?.length > 0 &&
                        i?.ExtensionProperties[index]?.Value?.LongValue !== 0
                    ) {
                        return i?.ExtensionProperties[index]?.Value?.LongValue !== this.props.context.request.apiSettings.channelId;
                    }
                }
                return false;
            });

        const viewProps: ISearchViewProps = {
            ...(this.props as IMoeSearchProps<IMoeSearchData>),

            Search: {
                moduleProps: this.props,
                className: `${classnames('ms-search', className)} ${disableSubmitSearch ? 'no-submit' : 'with-submit'}`
            },
            AutoSuggestAriaLabel: {
                tag: 'div',
                className: 'msc-autoSuggest__screen-reader',
                'aria-live': 'assertive',
                ref: this.autoSuggestResultScreenReaderDiv
            },
            AutoSuggestAriaLabelText: this.state.autoSuggestAriaLabel,
            searchText: this.state.searchText,
            AutoSuggest: {
                ref: this.autoSuggestResultDiv,
                className: 'ms-search__autoSuggest'
            },
            KeywordSuggest: {
                className: searchKeywordSuggestClass
            },
            ProductSuggest: {
                className: searchProductSuggestClass
            },
            CategorySuggest: {
                className: searchCategorySuggestClass
            },
            CurriculaSuggest: {
                className: searchCurriculaSuggestClass
            },
            SearchForm: {
                className: `${this.searchFormClass} ${isSearchFormExpanded ? 'bx-hide' : 'bx-hide'}`
            },
            FormWrapper: {
                tag: 'form',
                ref: this.formReference,
                className: 'ms-search__searchForm',
                'aria-label': 'Search',
                name: 'searchForm',
                role: 'search',
                action: searchURL,
                autoComplete: 'off',
                onSubmit: this._handleSubmit
            },
            UlKeyword: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__keywordResults-items', className)
            },
            UlProduct: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__productResults-items', className)
            },
            UlCategory: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__categoryResults-items', className)
            },
            label: LabelComponent({
                isSearchFormExpanded,
                searchLabelClass: `${this.searchLabelClass} ${isSearchFormExpanded ? 'bx-show' : 'bx-show'}`,
                searchtextInMaori,
                searchLabelArialLabelInMaori,
                searchCollapsedLabel: this.searchCollapsedLabel,
                hideSearchLabel,
                handleCancelSearchChange: this._handleCancelSearchChange,
                telemetryContent: this.telemetryContent
            }),
            form: FormComponent({
                ariaLabelCancel: cancelBtnAriaLabelInMaori,
                ariaLabelSearch: searchBtnAriaLabelInMaori,
                ariaLabelSubmit: submitBtnAriaLabelInMaori,
                hideSearchLabel,
                searchTextInput: this.searchTextInput,
                disableSubmitSearch,
                handleInputChange: this._handleInputChange,
                handleCancelSearchChange: this._handleCancelSearchChange,
                maxChars: this.maxChars,
                searchQsp,
                searchPlaceholder,
                query: searchText
            }),
            autosuggestKeyword:
                searchText &&
                searchText.length > 0 &&
                (isLoadingAutoSuggest || (keywordSuggestions && ArrayExtensions.hasElements(keywordSuggestions)))
                    ? KeywordSuggestionsComponent({
                          searchKeywordSuggestClass,
                          searchPageURL,
                          searchQsp,
                          hitPrefix: this.hitPrefix,
                          hitSuffix: this.hitSuffix,
                          autoSuggestProps: this.props,
                          searchText,
                          suggestions: keywordSuggestions,
                          telemetryContent: this.telemetryContent
                      })
                    : undefined,
            autosuggestProduct:
                searchText &&
                searchText.length > 0 &&
                (isLoadingAutoSuggest || (productSuggestions && ArrayExtensions.hasElements(productSuggestions)))
                    ? ProductSuggestionsComponent({
                          searchProductSuggestClass,
                          searchPageURL,
                          searchQsp,
                          hitPrefix: this.hitPrefix,
                          hitSuffix: this.hitSuffix,
                          autoSuggestProps: this.props,
                          searchText,
                          suggestions: productSuggestions,
                          telemetry: this.props.telemetry,
                          moduleType: this.props.typeName,
                          moduleId: this.props.id,
                          telemetryContent: this.telemetryContent,
                          preferredLanguage: preferredLanguage
                      })
                    : undefined,
            autosuggestCategory:
                searchText &&
                searchText.length > 0 &&
                (isLoadingAutoSuggest || (categorySuggestions && ArrayExtensions.hasElements(categorySuggestions)))
                    ? CategorySuggestionsComponent({
                          config: this.props.config,
                          searchCategorySuggestClass,
                          searchPageURL,
                          searchQsp,
                          hitPrefix: this.hitPrefix,
                          hitSuffix: this.hitSuffix,
                          autoSuggestProps: this.props,
                          searchText,
                          suggestions: categorySuggestions,
                          searchRefiners,
                          telemetryContent: this.telemetryContent,
                          shouldShowFullPath: shouldShowFullCategoryPath
                      })
                    : undefined,
            autosuggestOtherCurricula:
                searchText &&
                searchText.length > 0 &&
                (isLoadingAutoSuggest || (otherCurriculaSuggestions && ArrayExtensions.hasElements(otherCurriculaSuggestions)))
                    ? OtherCurriculaSuggestionsComponent({
                          searchCurriculaSuggestClass,
                          searchPageURL,
                          searchQsp,
                          hitPrefix: this.hitPrefix,
                          hitSuffix: this.hitSuffix,
                          autoSuggestProps: this.props,
                          searchText,
                          suggestions: otherCurriculaSuggestions,
                          telemetry: this.props.telemetry,
                          moduleType: this.props.typeName,
                          moduleId: this.props.id,
                          telemetryContent: this.telemetryContent
                      })
                    : undefined,
            callbacks: {
                handleCancelSearchChange: this._handleCancelSearchChange
            },
            isSearchFormExpanded: this.state.isSearchFormExpanded,
            isLoadingAutoSuggest,
            isLoadingNode: this._isLoadingNode()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Function to create the ReactNode for loading Autosuggest result.
     * @returns ReachNode for loading Autosuggest result.
     */
    private readonly _isLoadingNode = (): React.ReactNode => {
        const { context } = this.props;

        // Identifying Language
        const toggleLang = getToggleLang(context);
        const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
        const preferredLanguage: any = getUserPreferredLanguage(KWR, SEARCH, userPrefLang, toggleLang, context);

        const loadingText =
            preferredLanguage === 'maori'
                ? this.props.resources.autoSuggestResultLoadingMessageInMaori
                : this.props.resources.autoSuggestResultLoadingMessage;
        return <Spinner className='msc-autoSuggest__loadingResult' label={loadingText} size={SpinnerSize.medium} labelPosition='right' />;
    };

    private readonly _focusOutTarget = (e: any): void => {
        const { suggestions } = this.state;

        if (suggestions && suggestions.AllSearchResults && e.target.closest('.ms-search') === null) {
            this._clearAutoSuggestState();
        }
    };

    private readonly _keyup = (e: KeyboardEvent): void => {
        const { suggestions } = this.state;

        if (suggestions && suggestions.AllSearchResults && e.keyCode === KeyCodes.Escape) {
            this._clearAutoSuggestState();
        } else if (this.state.isSearchFormExpanded && e.keyCode === KeyCodes.Escape) {
            if (this.state.isSearchFormExpanded) {
                this.setState({ isSearchFormExpanded: false });
            }
        }

        if (
            !(
                (this.formReference.current && this.formReference.current.contains((e.target as unknown) as Node)) ||
                (this.autoSuggestResultDiv.current && this.autoSuggestResultDiv.current.contains((e.target as unknown) as Node))
            )
        ) {
            if (suggestions && suggestions.AllSearchResults) {
                this._clearAutoSuggestState();
            }

            if (this.state.isSearchFormExpanded) {
                this.setState({ isSearchFormExpanded: false });
            }
        }
    };

    private readonly _clearAutoSuggestState = (): void => {
        const {
            context: { actionContext },
            data: { searchState }
        } = this.props;

        if (this.searchTextInput.current && this.searchTextInput.current.value.length > 0) {
            searchState.searchText = '';
            searchState.defaultBasicSearchUrl = '';
            searchState.searchQueryStringParameter = '';
            actionContext ? actionContext.update(createSearchStateInput(searchState), searchState) : null;
            this.searchTextInput.current.value = '';
        }

        this.setState({ searchText: '', suggestions: undefined, searchRefiners: undefined });
    };

    private readonly _handleInputChange = (query: string): void => {
        if (!query || query.length <= this.maxChars) {
            this.setState({ searchText: query });
        }

        if (this.cancellabelDebounce) {
            this.cancellabelDebounce.cancel();
        }

        const {
            telemetry,
            context,
            data: { searchState }
        } = this.props;

        if (context) {
            if (query && query.length > 0) {
                if (query.length > this.maxChars) {
                    return;
                }

                this.cancellabelDebounce = debounce(() => {
                    this.setState({ isLoadingAutoSuggest: true });
                    getSearchSuggestionsAction(
                        new AutoSuggestInput(
                            query,
                            this.topResultsCount,
                            this._getSuggestionType(this.inputSuggestionType),
                            this.hitPrefix,
                            this.hitSuffix
                        ),
                        context.actionContext
                    )
                        .then(results => {
                            console.log('suggestions in search', results);
                            this._getSearchRefiners(query)
                                .then(refiners => {
                                    this.setState({
                                        suggestions: results,
                                        searchRefiners: refiners,
                                        autoSuggestAriaLabel: this._setAutoSuggestResultCount(results),
                                        isLoadingAutoSuggest: false
                                    });
                                })
                                .catch(error => {
                                    this.setState({
                                        suggestions: results,
                                        searchRefiners: undefined,
                                        autoSuggestAriaLabel: this._setAutoSuggestResultCount(results)
                                    });
                                    telemetry.exception(error);
                                    telemetry.debug('Unable to get refiners results');
                                });
                        })
                        .catch(error => {
                            this.setState({
                                suggestions: undefined,
                                searchRefiners: undefined,
                                autoSuggestAriaLabel: this._setAutoSuggestResultCount()
                            });
                            telemetry.exception(error);
                            telemetry.debug('Unable to get auto suggest results');
                        });
                }, this.waitTime);

                setTimeout(() => {
                    this.cancellabelDebounce();
                }, 0);

                searchState.searchText = query;
            } else {
                this.setState({ suggestions: undefined, searchRefiners: undefined });
                searchState.searchText = '';
            }
            context.actionContext.update(createSearchStateInput(searchState), searchState);
        }
    };

    private readonly _getSuggestionType = (suggestionTypes?: ISuggestionTypeCriterionData[]): string => {
        if (suggestionTypes && suggestionTypes.length > 0) {
            const distinctSuggestionTypes: string[] = [];
            suggestionTypes &&
                suggestionTypes.forEach((suggestionTypeItem, index) => {
                    suggestionTypeItem.SuggestionType &&
                        distinctSuggestionTypes.push(
                            suggestionTypeItem.SuggestionType.charAt(0).toUpperCase() + suggestionTypeItem.SuggestionType.slice(1)
                        );
                });
            return distinctSuggestionTypes.join(',');
        }
        return 'Keyword, Product, ScopedCategory';
    };

    private async _getSearchRefiners(searchText: string): Promise<ProductRefiner[]> {
        const { actionContext: ctx } = this.props.context;
        return getProductSearchRefinersAsync(
            { callerContext: ctx },
            {
                SearchCondition: searchText,
                Context: {
                    ChannelId: +ctx.requestContext.apiSettings.channelId,
                    CatalogId: +ctx.requestContext.apiSettings.catalogId
                },
                Language: ctx.requestContext.locale
            }
        );
    }

    private readonly _handleSubmit = (
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>
    ): void => {
        const {
            config: { disableSubmitSearch },
            context: {
                actionContext,
                app: {
                    config: { searchQueryStringParameter }
                }
            }
        } = this.props;

        const query = this.searchTextInput.current && this.searchTextInput.current.value;
        e.preventDefault();

        if (!query || (query && query.length === 0) || disableSubmitSearch) {
            this.searchTextInput && this.searchTextInput.current && this.searchTextInput.current.focus();
        } else {
            const searchQsp = searchQueryStringParameter || 'q';
            const searchURL = this._appendQueryParams(getUrlSync('search', actionContext) || '', query, searchQsp);
            document.location.href = searchURL;
        }
    };

    private readonly _handleCancelSearchChange = (): void => {
        const { isSearchFormExpanded, suggestions } = this.state;

        if (!isSearchFormExpanded) {
            this.setState({
                isSearchFormExpanded: true,
                searchText: '',
                isInitialState: false
            });
        } else {
            if (suggestions && suggestions.AllSearchResults) {
                this._clearAutoSuggestState();
            }

            this.setState({ isSearchFormExpanded: false });
        }
    };

    private readonly _appendQueryParams = (route: string, query: string, qsp: string): string => {
        if (!MsDyn365.isBrowser) {
            return '';
        }

        const queryUrl = new URL(route, window.location.href);
        if (qsp && query) {
            queryUrl.searchParams.set(qsp, query);
        }

        return queryUrl.href;
    };

    private readonly _setAutoSuggestResultCount = (result?: Autosuggestions) => {
        if (this.autoSuggestResultScreenReaderDiv.current !== null) {
            const ariaLiveRegion = this.autoSuggestResultScreenReaderDiv.current;
            const suggestResultString = this.props.resources.autoSuggestFoundMessageInMaori;
            const emptyResultString = this.props.resources.noAutoSuggestionMessageInMaori;
            ariaLiveRegion.innerText =
                result && result.AllSearchResults && result.AllSearchResults.length > 0 ? suggestResultString : emptyResultString;
            ariaLiveRegion.setAttribute('aria-live', 'assertive');
            return ariaLiveRegion.innerText;
        }
        return '';
    };
}

export default Search;
